import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { documentService } from '../services/api';
import {
  FileText, Upload, Download, Trash2, Loader2, CheckCircle2,
  AlertCircle, Inbox, ChevronRight, File as FileIcon,
} from 'lucide-react';

// Friendly labels for the shared backend DocumentType enum (no duplication of source of truth).
const DOC_TYPES = [
  { value: 'cdl_front', label: 'CDL (Front)' },
  { value: 'cdl_back', label: 'CDL (Back)' },
  { value: 'medical_certificate', label: 'Medical Certificate' },
  { value: 'mvr_report', label: 'MVR Report' },
  { value: 'drug_test', label: 'Drug Test' },
  { value: 'clearinghouse_query', label: 'Clearinghouse Query' },
  { value: 'certificate_of_insurance', label: 'Certificate of Insurance (COI)' },
  { value: 'w9_form', label: 'W-9 Form' },
  { value: 'dispatch_agreement', label: 'Dispatch Agreement' },
  { value: 'lease_on_agreement', label: 'Lease-On Agreement' },
  { value: 'ifta_license', label: 'IFTA License' },
  { value: 'irp_registration', label: 'IRP Registration' },
  { value: 'other', label: 'Other' },
];
const TYPE_LABEL = Object.fromEntries(DOC_TYPES.map((t) => [t.value, t.label]));

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};

const DocumentCenter = ({ title, subtitle, isAdmin = false }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('other');
  const [docTitle, setDocTitle] = useState('');
  const [docDesc, setDocDesc] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // {type:'success'|'error', msg}
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await documentService.getMyDocuments();
      setDocs(Array.isArray(data) ? data : []);
    } catch {
      setStatus({ type: 'error', msg: 'Failed to load documents.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { setStatus({ type: 'error', msg: 'Please choose a file to upload.' }); return; }
    setUploading(true); setProgress(0); setStatus(null);
    try {
      await documentService.uploadDocument(file, docType, {
        title: docTitle, description: docDesc,
        onProgress: (pct) => setProgress(pct),
      });
      setStatus({ type: 'success', msg: `"${file.name}" uploaded successfully.` });
      setFile(null); setDocTitle(''); setDocDesc('');
      const input = document.getElementById('dc-file-input');
      if (input) input.value = '';
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed. Please try again.';
      setStatus({ type: 'error', msg: detail });
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await documentService.downloadDocument(doc.id);
      if (res?.download_url) window.open(res.download_url, '_blank', 'noopener,noreferrer');
    } catch {
      setStatus({ type: 'error', msg: 'Could not open document. It may have been removed.' });
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    setDeletingId(doc.id); setStatus(null);
    try {
      await documentService.deleteDocument(doc.id);
      setStatus({ type: 'success', msg: `"${doc.filename}" deleted.` });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Delete failed.';
      setStatus({ type: 'error', msg: detail });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="document-center">
      {/* Breadcrumb / back */}
      <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
        <Link to={isAdmin ? '/admin/dashboard' : '/dashboard'} className="hover:text-gray-700" data-testid="dc-breadcrumb-home">
          {isAdmin ? 'Admin Panel' : 'Dashboard'}
        </Link>
        <ChevronRight className="w-4 h-4 mx-1" />
        <span className="text-gray-900 font-medium">{title}</span>
      </nav>

      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-7 h-7 text-blue-600" /> {title}
        </h1>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>

      {status && (
        <div
          role="alert"
          data-testid={`dc-status-${status.type}`}
          className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
            status.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {status.type === 'success' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
          <span>{status.msg}</span>
        </div>
      )}

      {/* Upload form */}
      <form onSubmit={handleUpload} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="dc-upload-form">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2"><Upload className="w-5 h-5 text-blue-600" /> Upload a Document</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="dc-file-input" className="block text-sm font-medium text-gray-700 mb-1">File <span className="text-red-500">*</span></label>
            <input
              id="dc-file-input"
              type="file"
              onChange={(e) => setFile(e.target.files[0] || null)}
              data-testid="dc-file-input"
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md border border-gray-300"
              aria-describedby="dc-file-help"
            />
            <p id="dc-file-help" className="text-xs text-gray-400 mt-1">PDF, images, or office docs · max 25 MB</p>
          </div>
          <div>
            <label htmlFor="dc-type" className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
            <select
              id="dc-type"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              data-testid="dc-type-select"
              className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="dc-title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input id="dc-title" type="text" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} data-testid="dc-title-input" placeholder="Optional title" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label htmlFor="dc-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input id="dc-desc" type="text" value={docDesc} onChange={(e) => setDocDesc(e.target.value)} data-testid="dc-desc-input" placeholder="Optional description" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        {uploading && (
          <div data-testid="dc-progress" aria-live="polite">
            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-xs text-gray-500 mt-1">Uploading… {progress}%</p>
          </div>
        )}

        <button
          type="submit"
          disabled={uploading || !file}
          data-testid="dc-upload-btn"
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading…' : 'Upload Document'}
        </button>
      </form>

      {/* Document list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Uploaded Documents</h2>
        </div>
        {loading ? (
          <div className="flex justify-center py-12" data-testid="dc-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : docs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center px-6" data-testid="dc-empty">
            <Inbox className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-gray-700 font-medium">No direct platform uploads yet</p>
            <p className="text-gray-500 text-sm mt-1">Use the form above to upload your first document. Governed Google Drive files are indexed separately under the <span className="font-medium">Google Drive</span> tab.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm" data-testid="dc-table">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3">Category</th>
                  {isAdmin && <th className="px-4 py-3">Uploader</th>}
                  <th className="px-4 py-3">Uploaded</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {docs.map((doc) => (
                  <tr key={doc.id} data-testid={`dc-row-${doc.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileIcon className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900 break-all">{doc.title || doc.filename}</p>
                          {doc.title && <p className="text-xs text-gray-400 break-all">{doc.filename}</p>}
                          {doc.description && <p className="text-xs text-gray-500">{doc.description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{TYPE_LABEL[doc.document_type] || doc.document_type}</td>
                    {isAdmin && <td className="px-4 py-3 text-gray-600 break-all">{doc.uploaded_by_email || '—'}</td>}
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(doc.uploaded_at)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtSize(doc.file_size)}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{(doc.mime_type || '').split('/').pop() || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-3">
                        <button type="button" onClick={() => handleDownload(doc)} data-testid={`dc-download-${doc.id}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" aria-label={`Open ${doc.filename}`}>
                          <Download className="w-4 h-4" /> Open
                        </button>
                        <button type="button" onClick={() => handleDelete(doc)} disabled={deletingId === doc.id} data-testid={`dc-delete-${doc.id}`} className="inline-flex items-center gap-1 text-red-600 hover:underline disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 rounded" aria-label={`Delete ${doc.filename}`}>
                          {deletingId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentCenter;
